import moment, { type Moment } from 'moment';
import { useState, useEffect } from 'react';

import { Input, type InputProps } from '../Input';

export interface InputDateProps extends Omit<InputProps, 'onChange' | 'value'> {
  value?: InputProps['value'] | Moment;
  onChange?: (date: Moment | null) => void;
}

export const InputDate = ({ onChange, ...props }: InputDateProps) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (props.value) {
      if (moment.isMoment(props.value)) {
        setValue(props.value.format('DD.MM.YYYY'));
      } else {
        setValue(props.value.toString());
      }
    } else {
      setValue('');
    }
  }, [props.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    const digitsOnly = newValue.replace(/\D/g, '');

    if (digitsOnly.length <= 2) {
      newValue = digitsOnly;
    } else if (digitsOnly.length <= 4) {
      newValue = digitsOnly.slice(0, 2) + '.' + digitsOnly.slice(2);
    } else {
      newValue =
        digitsOnly.slice(0, 2) +
        '.' +
        digitsOnly.slice(2, 4) +
        '.' +
        digitsOnly.slice(4, 8);
    }

    setValue(newValue);

    if (newValue.length === 10 && newValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      const date = moment(newValue, 'DD.MM.YYYY', true);
      if (date.isValid()) {
        onChange?.(date);
      }
    } else if (newValue.length === 0) {
      onChange?.(null);
    }
  };

  return (
    <Input
      {...props}
      value={value}
      onChange={handleChange}
      placeholder={props.placeholder || 'ДД.ММ.ГГГГ'}
      suffixIcon={<CalendarIcon />}
      maxLength={10}
      inputMode="numeric"
      regexp={/^(?!.*\.\.)[0-9.]*$/}
    />
  );
};

const CalendarIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    height="18"
    width="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M14.667 1.385h-2V.692c0-.183-.07-.36-.196-.49A.654.654 0 0012 0a.654.654 0 00-.471.203.706.706 0 00-.196.49v.692H4.667V.692c0-.183-.07-.36-.196-.49A.654.654 0 004 0a.654.654 0 00-.471.203.706.706 0 00-.196.49v.692h-2c-.353 0-.692.145-.942.405S0 2.402 0 2.77v13.845c0 .368.14.72.39.98s.59.405.943.405h13.334c.353 0 .692-.146.943-.405.25-.26.39-.612.39-.98V2.77c0-.367-.14-.72-.39-.979-.25-.26-.59-.405-.943-.405zM4.333 14.539a.973.973 0 01-.555-.176 1.03 1.03 0 01-.369-.466 1.076 1.076 0 01.217-1.131.99.99 0 01.512-.284.965.965 0 01.578.059c.183.078.339.211.449.382a1.067 1.067 0 01-.125 1.311.981.981 0 01-.707.305zm3.667 0a.973.973 0 01-.556-.176 1.03 1.03 0 01-.368-.466 1.076 1.076 0 01.217-1.131.99.99 0 01.512-.284.965.965 0 01.578.059c.182.078.339.211.448.382a1.067 1.067 0 01-.124 1.311.981.981 0 01-.707.305zm0-3.462a.974.974 0 01-.556-.175 1.031 1.031 0 01-.368-.466 1.076 1.076 0 01.217-1.132.99.99 0 01.512-.284.966.966 0 01.578.06c.182.078.339.21.448.382a1.067 1.067 0 01-.124 1.31.982.982 0 01-.707.305zm3.667 3.462a.973.973 0 01-.556-.176 1.03 1.03 0 01-.368-.466 1.075 1.075 0 01.217-1.131.99.99 0 01.512-.284.965.965 0 01.577.059c.183.078.34.211.45.382a1.067 1.067 0 01-.125 1.311.982.982 0 01-.707.305zm0-3.462a.974.974 0 01-.556-.175 1.03 1.03 0 01-.368-.466 1.076 1.076 0 01.217-1.132.989.989 0 01.512-.284.965.965 0 01.577.06c.183.078.34.21.45.382a1.067 1.067 0 01-.125 1.31.982.982 0 01-.707.305zm3-5.539H1.333V2.77h2v.693c0 .183.07.36.196.49.125.129.294.202.471.202a.654.654 0 00.471-.203.706.706 0 00.196-.49V2.77h6.666v.693c0 .183.07.36.196.49.125.129.294.202.471.202a.654.654 0 00.471-.203.706.706 0 00.196-.49V2.77h2v2.77z"
      fill="currentColor"
    />
  </svg>
);
